import { useEffect, useState } from "react";
import { Check, CloudOff, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { formatRelativeTime } from "../../lib/format";
import { useTimeZone } from "../../lib/timeZone";

/**
 * AutosaveIndicator — the shared "Saving… / Saved ✓ / last saved 2 min ago"
 * status used by the draft wizard and autosaving forms, so brokers/recipients
 * trust their progress is kept. While idle with a known last-saved time it
 * shows a live relative timestamp (refreshed each minute). Spinner respects
 * prefers-reduced-motion.
 */
export type AutosaveState = "idle" | "saving" | "saved" | "error";

export interface AutosaveIndicatorProps {
  state: AutosaveState;
  /** Instant of the last successful save — drives the idle "last saved …" line. */
  lastSavedAt?: string | Date | null;
  /** Copy shown when idle and nothing has been saved yet. */
  idleHint?: string;
  className?: string;
  "data-testid"?: string;
}

export function AutosaveIndicator({
  state,
  lastSavedAt,
  idleHint = "Your answers save automatically.",
  className,
  "data-testid": testId,
}: AutosaveIndicatorProps) {
  // The viewer's zone from the ambient design-system provider (NOT an app-layer
  // auth hook — see `lib/timeZone`). `null` when no provider is mounted, which
  // `formatRelativeTime` folds to FALLBACK_TIMEZONE itself. Only observable for
  // a `lastSavedAt` older than ~30 days, where the relative form gives way to an
  // absolute date.
  const tz = useTimeZone();

  // Re-render every 60s so the idle "last saved N minutes ago" stays current.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (state !== "idle" || !lastSavedAt) return;
    const handle = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(handle);
  }, [state, lastSavedAt]);

  let icon: React.ReactNode = null;
  let text = idleHint;
  let tone = "text-gray-mid";

  if (state === "saving") {
    icon = <Loader2 size={13} className="motion-safe:animate-spin" aria-hidden />;
    text = "Saving…";
  } else if (state === "saved") {
    icon = <Check size={13} aria-hidden />;
    tone = "text-status-success";
    text = "Saved ✓";
  } else if (state === "error") {
    icon = <CloudOff size={13} aria-hidden />;
    tone = "text-status-error";
    text = "Save failed — your typing is still on this device.";
  } else if (lastSavedAt) {
    const rel = formatRelativeTime(lastSavedAt, tz);
    if (rel) text = `Last saved ${rel}`;
  }

  return (
    <span
      className={cn("inline-flex items-center gap-space-1 text-caption", tone, className)}
      role="status"
      aria-live="polite"
      data-state={state}
      data-testid={testId}
    >
      {icon}
      {text}
    </span>
  );
}
